import { CheckCircle2, Info, XCircle } from '@tamagui/lucide-icons'
import { Toast, useToastState } from '@tamagui/toast'
import { GetProps, Stack, XStack, YStack } from 'tamagui'

// Define the variant type
type ToastVariant = 'neutral' | 'info' | 'positive' | 'negative'

// Define proper types for theme values
type ThemeColors = GetProps<typeof Stack>['backgroundColor']

// Helper function to get variant-specific properties
const getVariantProps = (variant: ToastVariant = 'neutral') => {
  const variants = {
    neutral: {
      backgroundColor: '$color3' as ThemeColors,
      borderColor: '$color6' as ThemeColors,
      color: '$color8' as ThemeColors,
      icon: null,
    },
    info: {
      backgroundColor: '#ECF9FC' as ThemeColors,
      borderColor: '#41BCDA' as ThemeColors,
      color: '#0791B3' as ThemeColors,
      icon: Info,
    },
    success: {
      backgroundColor: '$green2Light' as ThemeColors,
      borderColor: '$green8Light' as ThemeColors,
      color: '$green11Light' as ThemeColors,
      icon: CheckCircle2,
    },
    error: {
      backgroundColor: '$red2Light' as ThemeColors,
      borderColor: '$red8Light' as ThemeColors,
      color: '$red11Light' as ThemeColors,
      icon: XCircle,
    },
  }

  return variants[variant]
}

export const CustomToast = () => {
  const currentToast = useToastState()

  if (!currentToast || currentToast.isHandledNatively) {
    return null
  }

  const variant = (currentToast.variant as ToastVariant) || 'neutral'
  const variantProps = getVariantProps(variant)
  const IconComponent = variantProps.icon

  return (
    <Toast
      key={currentToast.id}
      duration={currentToast.duration}
      viewportName={currentToast.viewportName}
      enterStyle={{ opacity: 0, scale: 0.5, y: -25 }}
      exitStyle={{ opacity: 0, scale: 1, y: -20 }}
      y={0}
      opacity={1}
      scale={1}
      animation="quick"
      backgroundColor={variantProps.backgroundColor}
      borderColor={variantProps.borderColor}
      borderWidth="$1"
      shadowColor="$color10"
      shadowRadius="$4"
      shadowOffset={{ width: 0, height: 1 }}
      shadowOpacity={0.1}
      borderRadius="$6"
      paddingVertical="$2"
      paddingHorizontal="$4"
      maxWidth={400}
    >
      <XStack
        gap="$3"
        alignItems="flex-start"
        minHeight={currentToast.message ? 44 : 24}
        justifyContent="center"
      >
        {IconComponent && (
          <IconComponent
            size={20}
            color={variantProps.color as any}
            style={{ marginTop: 1, flexShrink: 0 }}
          />
        )}

        <YStack flex={1} gap="$1">
          <Toast.Title
            color={variantProps.color}
            fontSize="$3"
            fontWeight={currentToast.message ? 'bold' : '500'}
            letterSpacing={currentToast.message ? -0.2 : -0.3}
            allowFontScaling={false}
          >
            {currentToast.title}
          </Toast.Title>

          {!!currentToast.message && (
            <Toast.Description color={variantProps.color} allowFontScaling={false}>{currentToast.message}</Toast.Description>
          )}
        </YStack>
      </XStack>
    </Toast>
  )
}
